import React, {useCallback, useMemo, useState} from 'react'
import {useAllRentals} from '../rentals/gql/useAllRentals'
import {RentalsTable} from '../rentals/RentalsTable'
import {DASHBOARD_DAYS_BACK, dashboardInitialFromDate} from './dashboardUtils'
import {Box} from '../../webui/Box'
import {Paper} from '../../webui/Paper'
import {Typography} from '../../webui/Typography'

interface Props {}

const TABLE_PAGE_LIMIT = 40 // initial page size

export const RentalsThatMayNeedActionsTable: React.FC<Props> = () => {
  const [pageLimit] = useState<number>(TABLE_PAGE_LIMIT)

  const {
    data: {rentals = [], totalCount = 0} = {rentals: [], totalCount: 0},
    loading,
    error,
    fetchMore,
  } = useAllRentals({
    variables: {
      filter: {
        startDate: {
          after: dashboardInitialFromDate,
        },
        mayNeedAction: true,
      },
      pagination: {offset: 0, limit: pageLimit},
    },
    notifyOnNetworkStatusChange: true, // ensures loading state updates during fetchMore
  })

  const canFetchMore = useMemo(() => rentals.length < (totalCount ?? 0), [rentals.length, totalCount])

  const handleFetchMore = useCallback(() => {
    if (!canFetchMore) return
    void fetchMore({
      variables: {
        filter: {
          startDate: {
            after: dashboardInitialFromDate,
          },
          mayNeedAction: true,
        },
        pagination: {
          offset: rentals.length,
          limit: pageLimit,
        },
      },
    })
  }, [canFetchMore, fetchMore, rentals.length, pageLimit])

  return (
    <Paper>
      <RentalsTable
        error={error}
        loading={loading}
        rentals={rentals}
        searchEnabled={false}
        title={`Rentals that may need action last ${DASHBOARD_DAYS_BACK} days`}
      />
      <Box p={2} display="flex" justifyContent="center">
        {canFetchMore ? (
          <button
            onClick={handleFetchMore}
            disabled={loading}
            style={{
              padding: '8px 16px',
              borderRadius: 8,
              border: '1px solid #ccc',
              cursor: loading ? 'not-allowed' : 'pointer',
              background: loading ? '#f3f3f3' : 'white',
            }}
          >
            {loading ? 'Loading…' : 'Load more'}
          </button>
        ) : (
          <Typography variant="subtitle2" color="textSecondary">
            Showing all {rentals.length} of {totalCount}
          </Typography>
        )}
      </Box>
    </Paper>
  )
}
